import React from 'react';
import { Modal, Form, Input, Button, Typography, Space, Tag, Alert } from 'antd';
import { ExperimentOutlined, SendOutlined } from '@ant-design/icons';
import { useChannelStore } from '../../store/channelStore';
import { TestNotificationRequest } from '../../types/auth';

const { Title, Text } = Typography;
const { TextArea } = Input;

/**
 * 测试渠道模态框
 * 支持发送测试消息验证渠道配置
 */
const TestChannelModal: React.FC = () => {
  const {
    isTestModalVisible,
    testingChannel,
    loading,
    testChannel,
    closeTestModal,
  } = useChannelStore();

  const [form] = Form.useForm();

  // 获取渠道类型显示信息
  const getChannelTypeDisplay = (type: string) => {
    const typeMap: Record<string, { icon: string; color: string; name: string }> = {
      TELEGRAM: { icon: '📱', color: 'blue', name: 'Telegram' },
      DISCORD: { icon: '🎮', color: 'purple', name: 'Discord' },
      DINGTALK: { icon: '💼', color: 'orange', name: '钉钉' },
      FEISHU: { icon: '🚀', color: 'green', name: '飞书' },
      EMAIL: { icon: '📧', color: 'red', name: '邮件' },
      WEBHOOK: { icon: '🔗', color: 'gray', name: 'Webhook' },
      BARK: { icon: '🐕', color: 'cyan', name: 'Bark' },
    };

    const config = typeMap[type] || { icon: '❓', color: 'default', name: type };
    
    return (
      <Tag color={config.color} className="flex items-center">
        <span className="mr-1">{config.icon}</span>
        {config.name}
      </Tag>
    );
  };

  // 处理发送测试消息
  const handleSendTest = async () => {
    if (!testingChannel) return;

    try {
      const values = await form.validateFields();
      
      const testData: TestNotificationRequest = {
        title: values.title,
        body: values.body,
        url: values.url || undefined,
      };

      const success = await testChannel(testingChannel.id, testData);
      
      if (success) {
        closeTestModal();
        form.resetFields();
      }
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  // 处理取消
  const handleCancel = () => {
    closeTestModal();
    form.resetFields();
  };

  // 设置默认测试数据
  const setDefaultTestData = () => {
    form.setFieldsValue({
      title: '测试通知',
      body: '这是一条来自 DDDDAlert 的测试消息，如果您收到此消息，说明通知渠道配置正确！',
      url: 'https://github.com/DDDDAlert/DDDDAlert',
    });
  };

  if (!testingChannel) return null;

  return (
    <Modal
      title={
        <Space>
          <ExperimentOutlined />
          测试通知渠道
        </Space>
      }
      open={isTestModalVisible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          取消
        </Button>,
        <Button
          key="default"
          onClick={setDefaultTestData}
          className="mr-2"
        >
          使用默认内容
        </Button>,
        <Button
          key="send"
          type="primary"
          icon={<SendOutlined />}
          loading={loading}
          onClick={handleSendTest}
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            border: 'none',
          }}
        >
          发送测试消息
        </Button>,
      ]}
      width={600}
      destroyOnClose
    >
      {/* 渠道信息 */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <Text strong className="text-lg">{testingChannel.channel_name}</Text>
          {getChannelTypeDisplay(testingChannel.channel_type)}
        </div>
        <Text type="secondary" className="text-sm">
          渠道ID: {testingChannel.id}
        </Text>
        <div className="mt-2">
          <Text type="secondary" className="text-sm">
            状态: {testingChannel.is_enabled ? (
              <Tag color="green">已启用</Tag>
            ) : (
              <Tag color="red">已禁用</Tag>
            )}
          </Text>
        </div>
      </div>

      {/* 提示信息 */}
      <Alert
        message="测试说明"
        description="发送测试消息可以验证通知渠道是否配置正确。测试消息不会影响正常的监控通知。"
        type="info"
        showIcon
        className="mb-4"
      />

      {/* 测试表单 */}
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          title: '测试通知',
          body: '这是一条测试消息',
        }}
      >
        <Form.Item
          name="title"
          label="消息标题"
          rules={[
            { required: true, message: '请输入消息标题' },
            { max: 100, message: '标题不能超过100个字符' },
          ]}
        >
          <Input 
            placeholder="请输入测试消息的标题"
            showCount
            maxLength={100}
          />
        </Form.Item>

        <Form.Item
          name="body"
          label="消息内容"
          rules={[
            { required: true, message: '请输入消息内容' },
            { max: 500, message: '内容不能超过500个字符' },
          ]}
        >
          <TextArea
            placeholder="请输入测试消息的详细内容"
            rows={4}
            showCount
            maxLength={500}
          />
        </Form.Item>

        <Form.Item
          name="url"
          label="链接地址（可选）"
          rules={[
            { type: 'url', message: '请输入有效的URL地址' },
          ]}
        >
          <Input 
            placeholder="https://example.com（可选）"
            addonBefore="🔗"
          />
        </Form.Item>
      </Form>

      {/* 注意事项 */}
      <Alert
        message="注意事项"
        description={
          <ul className="list-disc list-inside space-y-1 mt-2 text-sm">
            <li>请确保通知渠道已正确配置并启用</li>
            <li>某些平台可能有发送频率限制</li>
            <li>如果测试失败，请检查凭证信息是否正确</li>
            <li>测试消息发送后，请检查对应平台是否收到通知</li>
          </ul>
        }
        type="warning"
        showIcon
        className="mt-4"
      />
    </Modal>
  );
};

export default TestChannelModal;
